import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Container,
    Grid,
    IconButton,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material/";
import LoadingButton from "@mui/lab/LoadingButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import actions from "../../../Store/actions";
import styles from "./styles";
import config from "../../../config";
import { logToSentry } from "../../../config/Sentry";

type Props = {
    inputProps: any,
    onChange: Function,
    onSuccess: Function,
};

const LoginForm = ({ onChange, onSuccess }: Props) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const classes = makeStyles(styles)();
    const { auth } = useSelector((state) => state);

    const [loading, setLoading] = useState(false);
    const [credentials, setCredentials] = useState({
        username: "",
        password: "",
    });
    const [passwordObfuscated, setPasswordObfuscated] = useState(true);
    const [isMounted, setIsMounted] = useState(true); // Mounted flag

    const { username, password } = credentials;
    const disableSubmit = loading || !(username && password);

    // Handle changes in input fields
    const handleChange =
        (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const updatedCredentials = {
                ...credentials,
                [name]: event.target.value,
            };
            setCredentials(updatedCredentials);
            onChange && onChange(updatedCredentials);
        };

    // Toggle password visibility
    const onPasswordObfuscate = () => setPasswordObfuscated((prev) => !prev);

    const resetPasswordUrl = React.useMemo(() => {
        const { username } = credentials;
        return [
            config.passwordReset.url,
            `?${config.passwordReset.params.join("&")}`,
            `&client_id=${config.passwordReset.clientId}`,
            `&redirect_uri=${encodeURIComponent(
                config.passwordReset.redirectUri
            )}`,
            `&lang=${i18n.language}`,
        ].join("");
    }, [credentials]); // Only depend on 'credentials' for reset password link

    // Open reset password URL in a new tab
    const onForgotPasswordClick = () =>
        window.open(resetPasswordUrl, "_blank", "noopener,noreferrer");

    // Submit form handler
    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!disableSubmit) {
            try {
                setLoading(true);
                const res = await dispatch(
                    actions.auth.create.getRequest({ username, password })
                );
                if (res && isMounted) {
                    localStorage.setItem("cvAuditorLang", res.account.language);
                    i18n.changeLanguage(res.account.language);
                }
                if (onSuccess && isMounted) onSuccess();
            } catch (error) {
                const errStr = error.toString();
                dispatch(actions.toast.create.error(t("errors.loginFailure")));
                logToSentry("auth:onSubmit", {
                    error,
                    errStr: error.toString(),
                });
                console.error(errStr);
            }
            if (isMounted) setLoading(false);
        }
    };

    // Initialize the form with the username from the auth state
    useEffect(() => {
        setIsMounted(true);
        // Update credentials only if the auth.username has changed
        setCredentials((prev) => {
            if (prev.username !== auth.username) {
                return { ...prev, username: auth.username };
            }
            return prev;
        });
        return () => setIsMounted(false); // Cleanup on unmount
    }, [auth.username]); // Only depend on auth.username

    return (
        <Container component="main" maxWidth="xs">
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                justifyContent="center"
            >
                <LockOutlinedIcon size="large" color="primary" />
                <Typography component="h1" variant="h5">
                    {t("signIn")}
                </Typography>
            </Stack>

            <Box className={classes.loginContainer}>
                <Box component="form" onSubmit={onSubmit} noValidate>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        name="email"
                        label={t("username")}
                        onChange={handleChange("username")}
                        placeholder={t("username")}
                        autoComplete="email"
                        autoFocus
                        value={username}
                        inputProps={{ "data-testid": "user-email" }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label={t("password")}
                        id="password"
                        color="primary"
                        type={passwordObfuscated ? "password" : "text"}
                        value={password}
                        onChange={handleChange("password")}
                        placeholder={t("password")}
                        autoComplete="current-password"
                        inputProps={{ "data-testid": "user-password" }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={onPasswordObfuscate}
                                    size="large"
                                    color="primary"
                                >
                                    {passwordObfuscated ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            ),
                        }}
                    />
                    <LoadingButton
                        id="login"
                        type="submit"
                        loading={loading}
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ my: 3 }}
                        onClick={onSubmit}
                        disabled={disableSubmit}
                        data-testid="login-submit"
                    >
                        {t("login")}
                    </LoadingButton>
                    <Grid container>
                        <Grid item xs />
                        <Grid item>
                            <Link
                                onClick={onForgotPasswordClick}
                                variant="body2"
                            >
                                {t("requestNewPassword")}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Container>
    );
};

export default LoginForm;
